<template>
  <div class="wr-content">
    <dash-greeting
      :user-name="userName"
      :period="period"
      :revenue-last24h="greetingRevenue24h"
      :insights-count="aiInsights.length"
      @period-change="onPeriodChange"
    />
    <dash-live-ticker :items="tickerItems" />
    <dash-kpi-strip :kpis="kpis" />
    <div class="wr-mid-grid">
      <dash-retention-chart
        :vsl-name="retentionVslName"
        :meta-data="retentionMeta"
        :retention-pts="retentionPts"
      />
      <dash-ai-insights :insights="aiInsights" :loading="aiLoading" />
    </div>
    <div class="wr-bot-grid">
      <dash-top-vsls :vsls="topVsls" />
      <dash-funnel :steps="funnelSteps" :revenue-value="funnelRevenueValue" :cpa-value="funnelCpaValue" />
    </div>
  </div>
</template>

<script>
import { useApi } from '@/composables/useApi'
import DashGreeting       from '@/components/dashboard/DashGreeting.vue'
import DashLiveTicker     from '@/components/dashboard/DashLiveTicker.vue'
import DashKpiStrip       from '@/components/dashboard/DashKpiStrip.vue'
import DashRetentionChart from '@/components/dashboard/DashRetentionChart.vue'
import DashAiInsights     from '@/components/dashboard/DashAiInsights.vue'
import DashTopVsls        from '@/components/dashboard/DashTopVsls.vue'
import DashFunnel         from '@/components/dashboard/DashFunnel.vue'

const api = useApi()
const fmt = n => n >= 1000 ? (n / 1000).toFixed(1).replace('.', ',') + 'k' : String(n)
const fmtPct = n => (n || 0).toFixed(1) + '%'
const fmtDate = d => d.toISOString().slice(0, 10)

// Backend BETWEEN expects: start_date=newer, end_date=older
function dateRange(period) {
  const now = new Date()
  const today = fmtDate(now)
  const ago = days => { const d = new Date(now); d.setDate(d.getDate() - days); return fmtDate(d) }
  if (period === 'today')     return { start_date: today,    end_date: today }
  if (period === 'yesterday') { const d = ago(1); return { start_date: d, end_date: d } }
  if (period === '7d')        return { start_date: today,    end_date: ago(6) }
  if (period === '30d')       return { start_date: today,    end_date: ago(29) }
  return { start_date: today, end_date: '2020-01-01' }
}

function prevRange(period) {
  const now = new Date()
  const ago = days => { const d = new Date(now); d.setDate(d.getDate() - days); return fmtDate(d) }
  if (period === 'today')     return { start_date: ago(1),  end_date: ago(1) }
  if (period === 'yesterday') return { start_date: ago(2),  end_date: ago(2) }
  if (period === '7d')        return { start_date: ago(7),  end_date: ago(13) }
  if (period === '30d')       return { start_date: ago(30), end_date: ago(59) }
  return null
}

function sumAnalytics(rows) {
  const load  = rows.reduce((s, r) => s + (Number(r.qtde_load) || 0), 0)
  const play  = rows.reduce((s, r) => s + (Number(r.qtde_play) || 0), 0)
  const eng   = play > 0
    ? rows.reduce((s, r) => s + (Number(r.engajamento) || 0) * (Number(r.qtde_play) || 0), 0) / play
    : 0
  const rate  = load > 0 ? (play / load) * 100 : 0
  return { load, play, rate, eng }
}

function sparkFrom(rows, key, n = 7) {
  if (!rows || !rows.length) return Array(n).fill(0)
  const slice = rows.slice(-n)
  while (slice.length < n) slice.unshift({ [key]: 0 })
  return slice.map(r => Number(r[key]) || 0)
}

function deltaStr(curr, prev) {
  if (!prev || prev === 0) return '—'
  const d = ((curr - prev) / prev) * 100
  return Math.abs(d).toFixed(1) + '%'
}

export default {
  name: 'WarRoomDashboard',
  components: {
    DashGreeting, DashLiveTicker,
    DashKpiStrip, DashRetentionChart, DashAiInsights, DashTopVsls, DashFunnel,
  },

  data() {
    return {
      period: '7d',
      // KPIs
      kpis: null,
      // Top VSLs
      topVsls: [],
      // Funnel
      funnelSteps: null,
      funnelRevenueValue: null,
      funnelCpaValue: null,
      greetingRevenue24h: null,
      // Retention
      retentionVslName: 'VSL Principal',
      retentionMeta: null,
      retentionPts: null,
      // Ticker
      tickerItems: [
        { action: 'Aguardando dados...', vsl: '—', time: '—' },
      ],
      // AI insights
      aiInsights: [],
      aiLoading: false,
    }
  },

  computed: {
    userData() {
      try { return JSON.parse(localStorage.getItem('userData') || '{}') } catch { return {} }
    },
    userName() {
      const d = this.userData
      if (d.user_name) return [d.user_name, d.user_sobrenome].filter(Boolean).join(' ')
      return d.fullName || d.username || 'Usuário'
    },
    userId() {
      return this.userData.id
    },
  },

  watch: {
    period() { this.fetchAll() },
  },

  mounted() {
    this.fetchAll()
    this.fetchAiInsights()
  },

  methods: {
    onPeriodChange(p) { this.period = p },

    async fetchAiInsights() {
      if (!this.userId) return
      this.aiLoading = true
      try {
        const r = await api.get(`/ai-insights/dashboard/${this.userId}`)
        this.aiInsights = r.data?.insights || []
      } catch (e) {
        console.warn('[WarRoom] ai-insights error', e)
      } finally {
        this.aiLoading = false
      }
    },

    async fetchAll() {
      if (!this.userId) return
      const range = dateRange(this.period)
      const prev  = prevRange(this.period)

      try {
        const [currRows, prevRows, vslList, leads] = await Promise.all([
          api.get(`/get/booster/getViewerDashBoard/${this.userId}`, { params: range }).then(r => r.data || []),
          prev ? api.get(`/get/booster/getViewerDashBoard/${this.userId}`, { params: prev }).then(r => r.data || []) : Promise.resolve([]),
          api.get(`/vslUserId/${this.userId}`).then(r => (r.data || []).filter(v => Number(v.vsl_ativo) === 1)),
          api.get(`/leads/${this.userId}`).then(r => r.data || []),
        ])

        this.buildKpis(currRows, prevRows)
        await this.buildTopVsls(vslList, range)
        this.buildFunnel(currRows, leads)
        this.buildTicker(leads)
        await this.buildRevenue24h()
        if (this.topVsls.length) {
          await this.buildRetention(this.topVsls[0], range)
        }
      } catch (e) {
        console.error('[WarRoom] fetchAll error', e)
      }
    },

    buildKpis(curr, prev) {
      const c = sumAnalytics(curr)
      const p = sumAnalytics(prev)
      const makeSpk = key => sparkFrom(curr, key)

      this.kpis = [
        {
          label: 'Visualizações', value: fmt(c.load),
          delta: deltaStr(c.load, p.load), pos: c.load >= p.load,
          color: 'blue', spark: makeSpk('qtde_load'),
        },
        {
          label: 'Plays', value: fmt(c.play),
          delta: deltaStr(c.play, p.play), pos: c.play >= p.play,
          color: 'orange', spark: makeSpk('qtde_play'),
        },
        {
          label: 'Play Rate', value: fmtPct(c.rate),
          delta: deltaStr(c.rate, p.rate), pos: c.rate >= p.rate,
          color: 'gold', spark: sparkFrom(curr, 'playRate'),
        },
        {
          label: 'Engajamento', value: fmtPct(c.eng),
          delta: deltaStr(c.eng, p.eng), pos: c.eng >= p.eng,
          color: 'green', spark: sparkFrom(curr, 'engajamento'),
        },
      ]
    },

    async buildTopVsls(vslList, range) {
      if (!vslList.length) { this.topVsls = []; return }

      const slice = vslList.slice(0, 10)
      const analytics = await Promise.all(
        slice.map(v =>
          api.get(`/get/booster/analytic/vslsData/${v.id}`, { params: range })
            .then(r => { const d = r.data; return Array.isArray(d) ? d[0] : d })
            .catch(() => null)
        )
      )

      const withData = slice.map((v, i) => ({
        id: v.id,
        name: v.vsl_name || `VSL ${v.id}`,
        type: 'VSL',
        duration: v.vsl_duration ? this.secToMin(v.vsl_duration) : '—',
        views: Number(analytics[i]?.qtde_load) || 0,
        eng: Math.round(Number(analytics[i]?.engajamento) || 0),
        trendUp: null,
        trend: '',
        barW: '0%',
      }))

      // Ordena por views desc
      withData.sort((a, b) => b.views - a.views)
      const maxViews = withData[0]?.views || 1

      this.topVsls = withData.slice(0, 5).map(v => ({
        ...v,
        views: fmt(v.views),
        barW: ((v.views / maxViews) * 100).toFixed(0) + '%',
      }))
    },

    buildFunnel(rows, leads) {
      const { load, play, eng } = sumAnalytics(rows)
      const engagedEst = Math.round(play * (eng / 100))
      const leadsCount = Array.isArray(leads) ? leads.length : 0
      const leadsRevenue = this.extractRevenueFromLeads(leads)

      this.funnelSteps = [
        { label: 'Carregou o player',            rawValue: load,        value: fmt(load),        drop: null },
        { label: 'Deu play',                     rawValue: play,        value: fmt(play),        drop: load > play ? `−${fmtPct((1 - play/load)*100)} · ${fmt(load - play)} não deram play` : null },
        { label: `Engajamento médio (${fmtPct(eng)})`, rawValue: engagedEst, value: fmt(engagedEst), drop: play > engagedEst ? `−${fmtPct((1 - engagedEst/play)*100)} · desistiram no meio` : null },
        { label: 'Converteu (lead)',              rawValue: leadsCount,  value: fmt(leadsCount),  drop: null },
      ]

      this.funnelRevenueValue = leadsRevenue > 0 ? this.formatBrl(leadsRevenue) : null
      this.funnelCpaValue = null
    },

    async buildRevenue24h() {
      try {
        const todayRange = dateRange('today')
        const leadsToday = await api.get(`/leads/${this.userId}`, { params: todayRange }).then(r => r.data || [])
        const revenue24h = this.extractRevenueFromLeads(leadsToday)
        this.greetingRevenue24h = revenue24h > 0 ? this.formatBrl(revenue24h) : null
      } catch (e) {
        this.greetingRevenue24h = null
      }
    },

    extractRevenueFromLeads(leads) {
      if (!Array.isArray(leads) || !leads.length) return 0

      const labelRegex = /(valor|value|amount|ticket|preco|price|venda|sale)/i
      const byConversation = new Map()

      for (const item of leads) {
        const id = String(item.id_conversa || item.id || '')
        if (!id) continue
        if (!labelRegex.test(String(item.label || ''))) continue
        const parsed = this.parseCurrencyLike(item.value)
        if (parsed <= 0) continue
        if (!byConversation.has(id)) byConversation.set(id, parsed)
      }

      return [...byConversation.values()].reduce((sum, v) => sum + v, 0)
    },

    parseCurrencyLike(raw) {
      if (raw === null || raw === undefined) return 0
      const source = String(raw).trim()
      if (!source) return 0

      // "1.234,56" => "1234.56", "1234.56" => "1234.56"
      const normalized = source
        .replace(/[^\d,.-]/g, '')
        .replace(/\.(?=\d{3}(\D|$))/g, '')
        .replace(',', '.')
      const value = Number(normalized)
      return Number.isFinite(value) ? value : 0
    },

    formatBrl(value) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
    },

    buildTicker(leads) {
      if (!Array.isArray(leads) || !leads.length) return
      const recent = [...leads]
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
        .slice(0, 6)
      this.tickerItems = recent.map(l => ({
        action: 'Lead capturado',
        vsl: l.page ? this.shortUrl(l.page) : (l.origem || 'site'),
        time: this.timeAgo(l.created_at),
      }))
    },

    async buildRetention(topVsl, range) {
      try {
        const r = await api.get(`/timeds/vsls/${topVsl.id}/stats`, { params: range })
        let pts = r.data || []

        // Se não há dados no período, tenta buscar sem filtro (all-time)
        if (!pts.length) {
          const fallback = await api.get(`/timeds/vsls/${topVsl.id}/stats`)
          pts = fallback.data || []
        }

        // Sem dados em nenhuma janela — limpa o gráfico
        if (!pts.length) {
          this.retentionPts = null
          this.retentionMeta = null
          return
        }

        const maxTotal = pts[0].total || 1
        const durSec = pts[pts.length - 1].timed || 0
        const halfway = pts.filter(p => p.total >= maxTotal * 0.5).length
        const pctHalf = durSec > 0 ? Math.round((halfway / pts.length) * 100) : 0

        this.retentionVslName = topVsl.name
        this.retentionMeta = {
          duracao: this.secToMin(durSec),
          retencaoMedia: fmtPct(pctHalf),
          viewersUnicos: fmt(maxTotal),
          completaram: fmt(pts[pts.length - 1].total || 0),
          completaramPct: maxTotal > 0 ? fmtPct(((pts[pts.length - 1].total || 0) / maxTotal) * 100) : '—',
        }
        this.retentionPts = pts
      } catch (e) {
        console.warn('[WarRoom] retention fetch error', e)
      }
    },

    secToMin(s) {
      const m = Math.floor(s / 60)
      const sec = String(s % 60).padStart(2, '0')
      return `${m}:${sec}`
    },

    shortUrl(url) {
      try { return new URL(url).pathname.replace(/\/$/, '') || url } catch { return url }
    },

    timeAgo(iso) {
      if (!iso) return 'agora'
      const diff = Math.floor((Date.now() - new Date(iso)) / 1000)
      if (diff < 60)   return `há ${diff}s`
      if (diff < 3600) return `há ${Math.floor(diff/60)}min`
      if (diff < 86400) return `há ${Math.floor(diff/3600)}h`
      return `há ${Math.floor(diff/86400)}d`
    },
  },
}
</script>

<style>
/* ─── Google Fonts ─────────────────────────────── */
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&display=swap');

/* ─── CSS Variables (defined at .layout-app level in LayoutApp.vue) ── */

/* ─── Utility ─────────────────────────────────── */
.wr-content .mono { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }

/* ─── Root ─────────────────────────────────────── */
.wr-content {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0;
  color: var(--wr-text);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  line-height: 1.5;
}

/* ─── Mid grid (retention + insights) ─────────── */
.wr-mid-grid {
  display: grid;
  grid-template-columns: 1fr 360px;
  gap: 20px;
  margin-bottom: 20px;
  align-items: start;
}

/* ─── Bottom grid (top vsls + funnel) ─────────── */
.wr-bot-grid {
  display: grid;
  grid-template-columns: 1.3fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

/* ─── Scrollbar ────────────────────────────────── */
.wr-scroll::-webkit-scrollbar { width: 6px; }
.wr-scroll::-webkit-scrollbar-track { background: transparent; }
.wr-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,.12); border-radius: 3px; }
.wr-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,.22); }

/* ─── Global keyframes ─────────────────────────── */
@keyframes wr-pulse {
  0%, 100% { opacity: 1; }
  50%       { opacity: .45; }
}
@keyframes wr-shimmer {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}

/* ─── Responsive ───────────────────────────────── */
@media (max-width: 1200px) {
  .wr-mid-grid { grid-template-columns: 1fr; }
  .wr-bot-grid { grid-template-columns: 1fr; }
}
@media (max-width: 900px) {
  .war-room { grid-template-columns: 1fr; }
  .war-room .dbs { display: none; }
  .wr-content { padding: 16px; }
}
</style>
